// Texto 25, etapa 2: recebimentos dos clientes (à vista, a prazo, sinal + restante), taxa da maquininha,
// parcelas com pagamento parcial, cobrança gentil, recibo, avisos (aniversário, sumido) e a ligação com Finanças.
// Cada venda/serviço é um registro 'receb' dentro do cliente (aba Meus clientes); as parcelas ficam dentro dele (c.parcelas).
import { getDoc, setDoc } from '../store/store';
import type { Reg } from '../store/types';
import { mudarReg, regsDe } from '../store/modulos';
import { addDays, today, uid } from './date';
import { dinheiro } from './moeda';
import { N_, t } from './i18n';

export type Parcela = { id: string; rotulo: string; valor: number; venc: string | null; recebido: number; pagoEm?: string; cobrancas?: number };
export const MODOS = [N_('À vista'), N_('A prazo'), N_('Sinal + restante')];
export const FORMAS = [N_('Pix'), N_('Dinheiro'), N_('Cartão de débito'), N_('Cartão de crédito à vista'), N_('Cartão de crédito parcelado'), N_('Boleto'), N_('Transferência'), N_('Fiado / a prazo')];

// ─── Ajustes "Como você recebe?" (D1/D3/C8/D7/D8) ───
export type ConfRec = { modos: string[]; taxas: Record<string, number>; sumidoDias: number; lembretes: number; nomeRecibo: string };
export function confRec(): ConfRec {
  const c = (getDoc('ajustes') as any).recebimentos ?? {};
  return { modos: Array.isArray(c.modos) && c.modos.length ? c.modos : [...MODOS], taxas: c.taxas ?? {}, sumidoDias: Number(c.sumidoDias) || 60,
    lembretes: c.lembretes == null ? 1 : Number(c.lembretes) || 0, nomeRecibo: String(c.nomeRecibo ?? '') };
}
export const mudarConfRec = (p: Partial<ConfRec>) => setDoc('ajustes', (d: any) => ({ ...d, recebimentos: { ...confRec(), ...p } }));
/** Quem só recebe à vista não vê a prazo, parcelas nem vermelho (D1). */
export const soAVista = () => { const m = confRec().modos; return m.length === 1 && m[0] === 'À vista'; };

/** A chave da taxa: "Cartão de crédito parcelado" em 3x vira "Crédito 3x". */
export function chaveTaxa(forma: string, n: number) {
  if (forma === 'Cartão de débito') return 'Débito';
  if (forma === 'Cartão de crédito à vista') return 'Crédito à vista';
  if (forma === 'Cartão de crédito parcelado') return `Crédito ${Math.max(2, n || 2)}x`;
  return '';
}
export const TAXAS_EDITAVEIS = ['Débito', 'Crédito à vista', ...Array.from({ length: 11 }, (_, i) => `Crédito ${i + 2}x`)];
/** D3: quanto cai de verdade ("R$ 100,00 no crédito 3x → recebe R$ 94,20"). */
export function liquido(c: Record<string, any>) {
  const k = chaveTaxa(String(c.forma ?? ''), Number(c.n) || 0);
  const tx = k ? confRec().taxas[k] ?? 0 : 0;
  const v = Number(c.valor) || 0;
  return { taxa: tx, chave: k, valor: Math.round(v * (1 - tx / 100) * 100) / 100 };
}

// ─── Parcelas ───
const r2 = (n: number) => Math.round(n * 100) / 100;
function addMeses(ymd: string, n: number) { const [a, m, d] = ymd.split('-').map(Number); const x = new Date(a, m - 1 + n, Math.min(d, 28)); const p = (k: number) => String(k).padStart(2, '0'); return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`; }
/** As parcelas que a venda deve ter, pelo jeito de pagar (D4/D5). À vista já nasce recebida. */
export function gerarParcelas(c: Record<string, any>): Parcela[] {
  const v = Number(c.valor) || 0;
  const data = String(c.data || today());
  if (c.como === 'A prazo') {
    const n = Math.max(1, Math.min(48, Math.round(Number(c.n) || 1)));
    const base = Math.floor((v / n) * 100) / 100;
    const ini = String(c.primeiroVenc || addMeses(data, 1));
    return Array.from({ length: n }, (_, i) => ({ id: uid(), rotulo: n > 1 ? `${i + 1}/${n}` : t('Parcela única'), valor: i === n - 1 ? r2(v - base * (n - 1)) : base, venc: addMeses(ini, i), recebido: 0 }));
  }
  if (c.como === 'Sinal + restante') {
    const sinal = Number(c.sinal) > 0 ? Number(c.sinal) : r2(v * (Number(c.sinalPct) || 50) / 100);
    return [{ id: uid(), rotulo: t('Sinal'), valor: r2(Math.min(sinal, v)), venc: String(c.sinalData || data), recebido: 0 },
      { id: uid(), rotulo: t('Restante'), valor: r2(Math.max(0, v - sinal)), venc: c.naEntrega ? null : String(c.restanteData || '') || null, recebido: 0 }];
  }
  return [{ id: uid(), rotulo: t('À vista'), valor: v, venc: data, recebido: v, pagoEm: data }];
}
export const parcelasDe = (r: Reg): Parcela[] => (Array.isArray(r.c.parcelas) ? r.c.parcelas : []);
export const falta = (p: Parcela) => r2(Math.max(0, p.valor - (Number(p.recebido) || 0)));
export const quitada = (p: Parcela) => falta(p) < 0.005;
export const atrasada = (p: Parcela, hoje = today()) => !quitada(p) && !!p.venc && p.venc < hoje;
export const vencendo = (p: Parcela, hoje = today()) => !quitada(p) && !!p.venc && p.venc >= hoje && p.venc <= addDays(hoje, 3);
/** A próxima data em aberto (para o My Planner e a ordem da lista). */
export const proxVenc = (ps: Parcela[]) => ps.filter(p => !quitada(p) && p.venc).map(p => p.venc!).sort()[0] ?? '';

/** Marca o que foi recebido de uma parcela (pode ser parcial, D4). valor null = o que falta inteiro; 0 = desmarca. */
export function receberParcela(mod: 'clientes', recId: string, parcelaId: string, valor: number | null) {
  mudarReg(mod, recId, r => {
    const ps = parcelasDe(r).map(p => {
      if (p.id !== parcelaId) return p;
      const novo = valor === 0 ? 0 : r2(Math.min(p.valor, (Number(p.recebido) || 0) + (valor ?? falta(p))));
      return { ...p, recebido: novo, pagoEm: novo > 0 ? today() : undefined };
    });
    return { ...r, c: { ...r.c, parcelas: ps, proxVenc: proxVenc(ps) } };
  });
}
export const contarCobranca = (recId: string, parcelaId: string) =>
  mudarReg('clientes', recId, r => ({ ...r, c: { ...r.c, parcelas: parcelasDe(r).map(p => (p.id === parcelaId ? { ...p, cobrancas: (p.cobrancas ?? 0) + 1 } : p)) } }));

// ─── Situação do cliente (C2/C6/E2/E3) ───
export const recebsDe = (clienteId: string) => regsDe('clientes').filter(r => r.col === 'receb' && r.pai === clienteId);
export function situacaoCliente(cliente: Reg) {
  const rs = recebsDe(cliente.id);
  const ps = rs.flatMap(r => parcelasDe(r).map(p => ({ p, r })));
  const deve = r2(ps.reduce((s, x) => s + falta(x.p), 0));
  const pago = r2(ps.reduce((s, x) => s + (Number(x.p.recebido) || 0), 0));
  const atrasadas = ps.filter(x => atrasada(x.p)), vencendoL = ps.filter(x => vencendo(x.p));
  const ultima = rs.map(r => String(r.c.data || r.criado.slice(0, 10))).sort().pop() ?? '';
  const proximo = ps.filter(x => !quitada(x.p) && x.p.venc && x.p.venc >= today()).sort((a, b) => a.p.venc!.localeCompare(b.p.venc!))[0] ?? null;
  const naEntrega = ps.some(x => !quitada(x.p) && !x.p.venc);
  const sinalPagoFaltaResto = rs.some(r => r.c.como === 'Sinal + restante' && quitada(parcelasDe(r)[0] ?? { valor: 1, recebido: 0 } as Parcela) && parcelasDe(r).slice(1).some(p => !quitada(p)));
  const aPrazo = rs.some(r => r.c.como === 'A prazo' || r.c.como === 'Sinal + restante');
  const conf = confRec();
  const sumido = !!ultima && ultima < addDays(today(), -conf.sumidoDias);
  const cor = !soAVista() && atrasadas.length ? '🔴' : vencendoL.length || sumido ? '🟡' : '🟢';
  return { rs, ps, deve, pago, atrasadas, vencendo: vencendoL, ultima, proximo, naEntrega, sinalPagoFaltaResto, aPrazo, sumido, cor };
}

// ─── Mensagens prontas (C7/C8/D7) ───
const primeiroNome = (c: Reg) => String(c.c.apelido || c.c.nome || '').trim().split(/\s+/)[0] ?? '';
export const msgAniversario = (c: Reg) => t('Oi, {nome}! Feliz aniversário! 🎉 Que o seu dia seja lindo. Um abraço!', { nome: primeiroNome(c) });
export const msgSumido = (c: Reg) => t('Oi, {nome}! Faz tempo que a gente não se fala. Posso ajudar em alguma coisa? Vai ser um prazer te atender de novo!', { nome: primeiroNome(c) });
export function msgCobranca(c: Reg, p: Parcela, moeda: string) {
  const v = dinheiro(falta(p), moeda);
  return p.venc && p.venc < today()
    ? t('Oi, {nome}! Passando para lembrar da parcela de {v} que venceu dia {d}. Qualquer coisa, me chama. Obrigada!', { nome: primeiroNome(c), v, d: p.venc.split('-').reverse().slice(0, 2).join('/') })
    : t('Oi, {nome}! Passando para lembrar da parcela de {v}{d}. Qualquer coisa, me chama. Obrigada!', { nome: primeiroNome(c), v, d: p.venc ? ' ' + t('que vence dia {d}', { d: p.venc.split('-').reverse().slice(0, 2).join('/') }) : '' });
}
